import { gql, useApolloClient } from "@apollo/client";
import { Classes, Intent, Placement } from "@blueprintjs/core";
import { DOCS_LINKS } from "@hex/common";
import React, { useCallback } from "react";
import styled from "styled-components";

import { DocsLink } from "../components/common/DocsLink";
import { Heading } from "../components/Heading";
import { HexButton, HexPopover } from "../hex-components";
import { useUserForMagic } from "../hooks/magicHooks.js";
import { useCurrentUser } from "../hooks/me/useCurrentUser";
import { TestSelectors } from "../util/testSelectors.js";

import { useUpdateHexMagicMutation } from "./MagicOnboardingPopover.generated";

const Wrapper = styled.div`
  max-width: 320px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
  color: ${({ theme }) => theme.fontColor.MUTED};

  p {
    strong {
      color: ${({ theme }) => theme.fontColor.DEFAULT};
    }
  }
`;

gql`
  mutation UpdateHexMagic($hexMagic: Boolean!) {
    updateHexMagic(enabled: $hexMagic) {
      id
      hexMagic
    }
  }
`;

export const MagicOnboardingWrapper: React.ComponentType<{
  onAccept?: () => void;
  onClose?: () => void;
}> = React.memo(function MagicOnboardingWrapper({ onAccept, onClose }) {
  const currentUser = useCurrentUser();
  const [updateHexMagic] = useUpdateHexMagicMutation();
  const apolloClient = useApolloClient();

  const setMagicOnboarded = useCallback(async () => {
    if (currentUser?.id) {
      await updateHexMagic({ variables: { hexMagic: true } });
      onAccept?.();
      await apolloClient.refetchQueries({ include: ["GetUserForMagic"] });
    }
    onClose?.();
  }, [apolloClient, currentUser, onAccept, onClose, updateHexMagic]);

  return (
    <Wrapper>
      <Heading renderAs="h1" styleAs="h3">
        Hex Agent
      </Heading>
      <p>
        By accepting, you acknowledge that you agree to the{" "}
        <DocsLink to={DOCS_LINKS.MagicTerms}>Terms and Conditions</DocsLink>.
      </p>
      <HexButton
        className={Classes.POPOVER_DISMISS}
        data-test={TestSelectors.MAGIC_ACCEPT_POPOVER}
        intent={Intent.PRIMARY}
        onClick={setMagicOnboarded}
      >
        Accept
      </HexButton>
    </Wrapper>
  );
});

export interface MagicOnboardingPopoverProps {
  children?: React.ReactChild;
  className?: string;
  placement?: Placement;
  fill?: boolean; // whether the popover should fill space or be inline
  isOpen?: boolean; // for controlled use only
  onClose?: () => void; // for controlled use only
  onAccept?: () => void;
}

export const MagicOnboardingPopover: React.ComponentType<MagicOnboardingPopoverProps> =
  React.memo(function MagicOnboardingPopover({
    children,
    className,
    fill,
    isOpen,
    onAccept,
    onClose,
    placement,
  }: MagicOnboardingPopoverProps) {
    const { onboardedToMagic } = useUserForMagic();

    return (
      <HexPopover
        autoFocus={true}
        canEscapeKeyClose={true}
        className={className}
        content={
          <MagicOnboardingWrapper onAccept={onAccept} onClose={onClose} />
        }
        css={fill ? "flex: 1" : "display: inline"}
        disabled={onboardedToMagic}
        enforceFocus={true}
        fill={true}
        isOpen={isOpen}
        minimal={true}
        placement={placement}
        usePortal={true}
        onClose={onClose}
      >
        <>{children}</>
      </HexPopover>
    );
  });
